/**
 * Placement state, shared between the frame-driven system and the UI.
 *
 * A module-level store rather than system config: IWSDK instantiates systems
 * itself, so this is the simplest way for the composition root to observe them
 * without threading callbacks through `registerSystem`.
 */
import { Store } from '../core/store.js';

export type PlacementPhase =
  /** Before the session starts. */
  | 'idle'
  /** Session running, tracking still settling — reticle suppressed. */
  | 'scanning'
  /** Reticle live; a pinch commits once the hit is stable. */
  | 'targeting'
  /** Guide is placed. */
  | 'placed';

export interface Pose {
  position: [number, number, number];
  quaternion: [number, number, number, number];
}

export interface PlacementState {
  phase: PlacementPhase;
  /** Where the guide was placed. Null until the first commit. */
  pose: Pose | null;
  /** True while the reticle is on a surface AND has been stable long enough. */
  locked: boolean;
}

export const placementStore = new Store<PlacementState>({
  phase: 'idle',
  pose: null,
  locked: false,
});

/** Ask the placement system to start over, e.g. "move my guide". */
export function requestReposition(): void {
  const { phase } = placementStore.getSnapshot();
  if (phase !== 'placed') return;
  // Skip the scan delay: tracking is already warm by now, and making the user
  // wait again after they have explicitly asked to move something reads as lag.
  placementStore.patch({ phase: 'targeting', locked: false });
}

const STORAGE_KEY = 'xr-guide.placement.v1';

/**
 * Persist the placed pose. Written in the anchor group's local space, so a
 * restored session puts the guide back on the same real-world surface rather
 * than the same coordinates.
 */
export function savePose(pose: Pose): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pose));
  } catch {
    // Private browsing or a full quota — placement still works this session.
  }
}

export function loadPose(): Pose | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isPose(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function clearPose(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing useful to do */
  }
}

function isPose(value: unknown): value is Pose {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<Pose>;
  return (
    Array.isArray(candidate.position) &&
    candidate.position.length === 3 &&
    candidate.position.every((n) => typeof n === 'number' && Number.isFinite(n)) &&
    Array.isArray(candidate.quaternion) &&
    candidate.quaternion.length === 4 &&
    candidate.quaternion.every((n) => typeof n === 'number' && Number.isFinite(n))
  );
}
